/**
 * Generates a thumbnail blob URL from a text-based file by rendering the first few lines to canvas.
 * Supports plain text, markdown, JSON, YAML, XML, code files, etc.
 *
 * @param file - Text file to generate thumbnail from
 * @returns Promise resolving to blob URL
 * @throws Error if thumbnail generation fails or if called in SSR context
 */
export async function generateTextThumbnail(file: File): Promise<string> {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		throw new Error('Text thumbnail generation requires browser environment');
	}

	// Read first 1000 characters of the file
	const text = await file.slice(0, 1000).text();
	const lines = text.split('\n').slice(0, 12); // First 12 lines for preview

	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) {
		throw new Error('Canvas 2D context unavailable');
	}

	const width = 200;
	const height = 200;
	canvas.width = width;
	canvas.height = height;

	context.fillStyle = '#FFFFFF';
	context.fillRect(0, 0, width, height);

	const fontSize = 10;
	const lineHeight = 14;
	const padding = 8;

	context.fillStyle = '#172B4D';
	context.font = `${fontSize}px SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`;
	context.textBaseline = 'top';

	lines.forEach((line, index) => {
		const y = padding + index * lineHeight;
		if (y + lineHeight > height - padding) {
			return;
		}

		// Truncate line if too long
		const maxWidth = width - padding * 2;
		let displayText = line;
		if (context.measureText(line).width > maxWidth) {
			// Truncate and add ellipsis
			while (context.measureText(displayText + '...').width > maxWidth && displayText.length > 0) {
				displayText = displayText.slice(0, -1);
			}
			displayText += '...';
		}

		context.fillText(displayText, padding, y);
	});

	// Add subtle border
	context.strokeStyle = '#DFE1E6';
	context.lineWidth = 1;
	context.strokeRect(0, 0, width, height);

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) {
				resolve(URL.createObjectURL(blob));
			} else {
				reject(new Error('Failed to generate blob from canvas'));
			}
		}, 'image/png');
	});
}
